import { Trans, useLingui } from '@lingui/react/macro';
import { addDays } from 'date-fns';
import _ from 'lodash';
import { Info, Paperclip } from 'lucide-react';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import applicationsResource from 'modules/application-link/domain/resources/applications.resource';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { mapPlayerToAnnounced } from 'modules/teams/domain/data-mappers/announced-teams.mapper';
import { getApplicationSaveErrorMessage } from 'modules/teams/domain/utils/application-save-error';
import { upsertChampionshipApplicationUsers } from 'modules/teams/store/championship-teams/actions';
import {
  announcedChampionshipTeamsDataSelector,
  updatingChampionshipTeamApplicationSelector,
} from 'modules/teams/store/championship-teams/selectors';
import { useGetTeamByIdQuery } from 'modules/teams/store/teams-api';
import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { trackEvent } from 'shared/analytics';
import { useMultiSelect } from 'shared/hooks';
import { AppDispatch } from 'store/store';
import EmptyButton from 'ud-ui/components/empty-button';
import TeamLogo from 'ud-ui/components/team-logo';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import useModalEditApplication from 'modules/teams/ui/hooks/use-modal-edit-application';
import ChampionshipApplicationFilters, {
  ChampionshipApplicationFiltersOption,
  ChampionshipTeamsFilterValues,
} from './championship-application-filters';
import CreateTeamPlayerForm from '../championship-team-players/create-team-player-form';
import { BodyBlock, HeadBlock } from './styles';
import TeamPlayerCard from './team-player-card';
import { theme } from 'styles/theme/theme';

const statusItems: ChampionshipApplicationFiltersOption<ApplicationStatus>[] = [
  {
    status: ApplicationStatus.PENDING,
    color: 'black',
  },
  {
    status: ApplicationStatus.ACCEPTED,
    color: '#58B879',
  },
  {
    status: ApplicationStatus.DECLINED,
    color: theme.colors.error,
  },
];

type ChampionshipTeamPlayersProps = {
  teamId: number;
  championshipId: number;
};

const ChampionshipTeamPlayers = ({ teamId, championshipId }: ChampionshipTeamPlayersProps) => {
  const { t, i18n } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const { open, Modal } = useModalEditApplication();
  const announcedTeams = useSelector(announcedChampionshipTeamsDataSelector(championshipId));
  const settings = useSelector(championshipSettingsSelector(championshipId));
  const updatingApplicationId = useSelector(updatingChampionshipTeamApplicationSelector);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const announcedTeam = announcedTeams ? announcedTeams[teamId] : null;

  const [status, selectStatus] = useState<ApplicationStatus>(ApplicationStatus.PENDING);
  const [name, setName] = useState<string>('');

  const { isLoading, data: detailTeam } = useGetTeamByIdQuery(teamId, { skip: !teamId });

  const mergedPlayers = useMemo(() => {
    if (isLoading || !announcedTeam || !detailTeam?.players) {
      return [];
    }

    const notRegisteredApplications = mapPlayerToAnnounced(detailTeam.players, {
      teamId,
      championshipId,
      teamCreatedAt: detailTeam.createdAt,
    });

    return _.uniqBy([...Object.values(announcedTeam.applications || []), ...notRegisteredApplications], 'player.id');
  }, [isLoading, announcedTeam, detailTeam?.players, detailTeam?.createdAt, teamId, championshipId]);

  const playersList = useMemo(() => {
    return mergedPlayers
      .filter((app) => !app.player.isReal)
      .filter((app) => status === app.status)
      .filter((app) => {
        const query = name.toLowerCase();
        const firstName = app.player.firstName.toLowerCase();
        const lastName = app.player.lastName.toLowerCase();

        return firstName.includes(query) || lastName.includes(query);
      });
  }, [mergedPlayers, status, name]);

  const {
    selectedIds: selectedPlayers,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseUp,
    handleSelect,
    setSelectedIds: setSelectedPlayers,
  } = useMultiSelect<PlayerApplication>({
    items: playersList,
    getId: (item: PlayerApplication) => item.player.id,
  });

  const countByStatus = useMemo(() => {
    const defaultValue = {
      [ApplicationStatus.PENDING]: 0,
      [ApplicationStatus.ACCEPTED]: 0,
      [ApplicationStatus.DECLINED]: 0,
    };

    return mergedPlayers
      .filter((app) => !app.player.isReal)
      .reduce<Record<string, number>>((result, item) => {
        const count = result[item.status] || 0;
        return {
          ...result,
          [item.status]: count + 1,
        };
      }, defaultValue);
  }, [mergedPlayers]);

  const handleFilterChange = useCallback((values: ChampionshipTeamsFilterValues<ApplicationStatus>) => {
    setName(values.name);
    selectStatus(values.status);
  }, []);

  const handlePlayersStatusChange = useCallback(
    async (status: ApplicationStatus.ACCEPTED | ApplicationStatus.DECLINED) => {
      if (!announcedTeam || selectedPlayers.length === 0) return;

      const applications = mergedPlayers.map((app) => ({
        userId: app.player.id,
        name: app.player.firstName,
        surname: app.player.lastName,
        middleName: app.player.middleName,
        birthDate: app.player.birthDate,
        position: app.position,
        number: app.number,
        state: selectedPlayers.includes(app.player.id) ? status : app.status,
      }));

      // Check max allowed players in application before accepting
      if (status === ApplicationStatus.ACCEPTED) {
        const max = settings?.applicationMaxPlayers ?? 28;
        if (typeof max === 'number') {
          const acceptedCount = applications.filter((a) => a.state === ApplicationStatus.ACCEPTED).length;
          if (acceptedCount > max) {
            toast.error(t`Заявляемое кол-во игроков не соответствует заявочной кампании. Сверьтесь с условиями турнира.`);
            return;
          }
        }
      }

      // Считаем до мутации: после неё в сторе уже новые статусы, и «сколько сменилось»
      // восстановить нечем. Учитываем только реальную смену — повторное принятие уже
      // заявленных игроков события не даёт.
      const newlyAccepted =
        status === ApplicationStatus.ACCEPTED
          ? mergedPlayers.filter((app) => selectedPlayers.includes(app.player.id) && app.status !== ApplicationStatus.ACCEPTED).length
          : 0;

      setIsBulkUpdating(true);
      try {
        await dispatch(
          upsertChampionshipApplicationUsers({
            teamId,
            championshipId,
            applications,
          }),
        ).unwrap();

        StepperMainTool.setByPath('stepper:main:applicationTeam', true);
        trackEvent('Team Application Submitted', { championship_id: championshipId, team_id: teamId, flow: 'v1', source: 'bulk' });
        if (newlyAccepted > 0) {
          trackEvent('Player Application Accepted', {
            championship_id: championshipId,
            team_id: teamId,
            flow: 'v1',
            source: 'bulk',
            players_count: newlyAccepted,
          });
        }
        toast.success(t`Игроки успешно обновлены`);
        setSelectedPlayers([]);
      } catch (error) {
        // Сервер ничего не сохранил — выбор и статусы на экране оставляем как были.
        toast.error(getApplicationSaveErrorMessage(error, t`Не удалось обновить заявки игроков`));
      } finally {
        setIsBulkUpdating(false);
      }
    },
    [
      t,
      dispatch,
      teamId,
      championshipId,
      announcedTeam,
      selectedPlayers,
      setSelectedPlayers,
      settings?.applicationMaxPlayers,
      mergedPlayers,
    ],
  );

  const handleCopyLink = useCallback(async () => {
    const endDate = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : undefined;
    if (endDate) {
      const now = new Date();
      if (endDate < now) {
        toast.warning(t`Заявочная кампания закончилась. Чтобы получить ссылку, продлите дату окончания приёма заявок.`);
        return;
      }
    }

    const token = await applicationsResource.getToken(championshipId, teamId);
    const params = new URLSearchParams();
    params.append('token', token.token);
    const link = `${window.location.origin}${Pages.APPLICATION_LINK.INDEX}?${params.toString()}`;
    navigator.clipboard.writeText(link);
    const expiresAt = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : addDays(new Date(), 1);
    const expiresDate = expiresAt.toLocaleDateString(i18n.locale);
    const expiresTime = expiresAt.toLocaleTimeString(i18n.locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
    toast.success(t`Ссылка скопирована и будет активна до ${expiresDate} ${expiresTime}`);
  }, [championshipId, settings?.applicationEndDate, teamId, t, i18n.locale]);

  const body = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex-center h-32 w-full">
          <ClipLoader />
        </div>
      );
    }

    if (playersList.length === 0) {
      return (
        <div className="flex-center my-5 h-full">
          <UDText type="body-text" className="text-surfaceRomanSilver30">
            <Trans>Список пуст</Trans>
          </UDText>
        </div>
      );
    }

    return (
      <>
        {playersList.map((app, index) => (
          <TeamPlayerCard
            key={app.player.id}
            index={index + 1}
            teamId={app.teamId}
            player={app}
            isUpdating={updatingApplicationId === app.id || isBulkUpdating}
            isSelected={selectedPlayers.includes(app.player.id)}
            onSelect={handleSelect}
            onMouseDownCheckbox={(event) => handleMouseDown(app.player.id, event)}
            onMouseEnterCheckbox={() => handleMouseEnter(app.player.id)}
            onMouseLeaveCheckbox={() => handleMouseLeave(app.player.id)}
            onClick={() => open(app, teamId)}
            applicationMaxPlayers={settings?.applicationMaxPlayers ?? 28}
            applications={announcedTeam?.applications}
          />
        ))}
      </>
    );
  }, [
    announcedTeam?.applications,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleSelect,
    isBulkUpdating,
    isLoading,
    open,
    playersList,
    selectedPlayers,
    settings?.applicationMaxPlayers,
    teamId,
    updatingApplicationId,
  ]);

  if (!announcedTeam) {
    return null;
  }

  const startDate = settings?.applicationStartDate ? new Date(settings.applicationStartDate) : undefined;
  const endDate = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : undefined;
  const now = new Date();
  const isAfterStartDate = startDate ? startDate < now : true;
  const isBeforeEndDate = endDate ? endDate > now : true;

  const isRangeValid = isAfterStartDate && isBeforeEndDate;

  const playerIds = announcedTeam.applications.map((app) => app.player.id);
  const startDateLabel = startDate?.toLocaleDateString(i18n.locale);
  const endDateLabel = endDate?.toLocaleDateString(i18n.locale);

  return (
    <div className="mt-4 w-full px-6" onMouseUp={handleMouseUp}>
      <HeadBlock>
        <div className={'flex justify-between'}>
          <div className={'flex items-center'} style={{ gap: 17 }}>
            <TeamLogo size={60} url={announcedTeam.logo?.url} />
            <UDText type={'subhead'}>{announcedTeam.name}</UDText>
          </div>
          <div className="flex items-center" style={{ gap: 10 }}>
            <EmptyButton
              icon={<Paperclip size={16} />}
              color={theme.colors.EditorAccentBlue}
              style={{ gap: 6 }}
              onClick={handleCopyLink}
              disabled={!isRangeValid}
            >
              <UDText type={'subhead'} style={{ color: 'inherit' }}>
                <Trans>Скопировать ссылку для заявки</Trans>
              </UDText>
            </EmptyButton>
            {!isRangeValid && (
              <UDTippy
                interactive
                content={
                  <UDText type="subhead">
                    {!isAfterStartDate && (
                      <div>
                        <Trans>
                          <Link
                            className="text-LightBlue"
                            to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
                            state={{ highlight: { applicationStartDate: true } }}
                          >
                            Заявочная кампания
                          </Link>{' '}
                          ещё не началась. Дата начала {startDateLabel}. Для копирования ссылки измените дату начала приёма заявок.
                        </Trans>
                      </div>
                    )}
                    {!isBeforeEndDate && (
                      <div>
                        <Trans>
                          <Link
                            className="text-LightBlue"
                            to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
                            state={{ highlight: { applicationEndDate: true } }}
                          >
                            Заявочная кампания
                          </Link>{' '}
                          закончилась. Дата окончания {endDateLabel}. Для копирования ссылки продлите дату окончания приёма заявок.
                        </Trans>
                      </div>
                    )}
                  </UDText>
                }
              >
                <div>
                  <UDText type="subhead">⚠️</UDText>
                </div>
              </UDTippy>
            )}
            <UDTippy
              interactive
              content={
                <UDText type="subhead">
                  <Trans>
                    Ссылка для заявки будет скопирована в буфер обмена. Отправьте её в чат, чтобы игроки могли подать заявку на участие
                    в турнире.
                    <br />
                    ⚠️Срок ссылки до даты окончания приёма заявок в{' '}
                    <Link
                      className="text-LightBlue"
                      to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
                      state={{ highlight: { applicationEndDate: true } }}
                    >
                      заявочной кампании
                    </Link>{' '}
                    или 24 часа с момента создания ссылки, если дата не указана.
                  </Trans>
                </UDText>
              }
            >
              <Info size={16} />
            </UDTippy>
          </div>
        </div>
        <CreateTeamPlayerForm teamId={teamId} championshipId={championshipId} existPlayerIds={playerIds} />
      </HeadBlock>
      <BodyBlock>
        <div style={{ paddingInline: 20 }}>
          <ChampionshipApplicationFilters<ApplicationStatus>
            selectedStatus={status}
            name={name}
            options={statusItems}
            inputPlaceholder={t`ФИО игрока`}
            countByStatus={countByStatus}
            onChange={handleFilterChange}
            selectedIds={selectedPlayers}
            onAccept={async () => await handlePlayersStatusChange(ApplicationStatus.ACCEPTED)}
            onDecline={async () => await handlePlayersStatusChange(ApplicationStatus.DECLINED)}
          />
        </div>
        <div
          style={{
            marginTop: 10,
            paddingInline: 20,
          }}
        >
          {body}
        </div>
      </BodyBlock>
      <Modal />
    </div>
  );
};

export default ChampionshipTeamPlayers;
